import {
  ActionIcon,
  Button,
  Card,
  Group,
  Modal,
  MultiSelect,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { IconArrowDown, IconArrowUp, IconPlus, IconTrash } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';

import prettifiedLabel from '../../constants/labelPrettifier';
import {
  blankFunnelStep,
  draftToFunnelPayload,
  FUNNEL_EVENT_MAPPINGS,
  funnelSegmentLabel,
  funnelToDraft,
  MAX_FUNNEL_STEPS,
  MIN_FUNNEL_STEPS,
  moveFunnelStep,
  STEP_KIND_ACTIONS,
  STEP_KIND_EVENT_MAPPING,
  validateFunnelDraft,
} from '../../helpers/funnels';
import getErrorMessage from '../../helpers/getErrorMessage';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import Spinner from '../Spinner';

const eventMappingOptions = FUNNEL_EVENT_MAPPINGS.map((mapping) => ({
  value: mapping,
  label: prettifiedLabel(mapping),
}));

const stepKindOptions = [
  { value: STEP_KIND_EVENT_MAPPING, label: 'Standard event' },
  { value: STEP_KIND_ACTIONS, label: 'Event actions' },
];

/**
 * Create/edit form for one funnel. Rendered only while open, so it loads the
 * domain's event actions when the user actually opens it.
 *
 * @param {object} [funnel] - the funnel being edited; absent when creating
 * @param {Array<{id: number, name: string}>} segments - the domain's behavior segments
 * @param {Function} onClose
 * @param {Function} onSaved
 */
const FunnelFormModal = ({ funnel, segments, onClose, onSaved }) => {
  const fetch = useFetch();
  const { accountId, domainId } = useParams();

  const [draft, setDraft] = useState(() => funnelToDraft(funnel));
  const [eventActions, setEventActions] = useState();
  const [error, setError] = useState();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;

    fetch(`/api/admin/accounts/${accountId}/domains/${domainId}/eventActions`)
      .then(({ eventActions }) => {
        if (!cancelled) setEventActions(eventActions);
      })
      .catch((err) => {
        if (!cancelled) setError(getErrorMessage(err));
      });

    return () => {
      cancelled = true;
    };
  }, [fetch, accountId, domainId]);

  const updateStep = (index, changes) =>
    setDraft((current) => ({
      ...current,
      steps: current.steps.map((step, position) => (position === index ? { ...step, ...changes } : step)),
    }));

  const setSteps = (steps) => setDraft((current) => ({ ...current, steps }));

  // The draft's segmentId may not be in the loaded `behavior` segments - it was
  // deleted, or its scope changed - the same case funnelSegmentLabel exists for
  // on the card. Append a synthetic option so the Select shows that id instead
  // of silently falling back to the "All profiles" placeholder while still
  // saving the old id (draftToFunnelPayload keeps it as-is).
  const segmentOptions = segments.map((segment) => ({ value: segment.id.toString(), label: segment.name }));
  if (draft.segmentId !== null && !segments.some((segment) => segment.id === draft.segmentId)) {
    segmentOptions.push({ value: draft.segmentId.toString(), label: funnelSegmentLabel(segments, draft.segmentId) });
  }

  const save = (event) => {
    event.preventDefault();

    const invalid = validateFunnelDraft(draft);
    if (invalid) {
      setError(invalid);
      return;
    }

    setError(undefined);
    setSaving(true);

    const funnels = `/api/admin/accounts/${accountId}/domains/${domainId}/funnels`;
    fetch(funnel ? `${funnels}/${funnel.id}` : funnels, {
      method: funnel ? 'PUT' : 'POST',
      body: JSON.stringify(draftToFunnelPayload(draft)),
    })
      .then(onSaved)
      .catch((err) => {
        // The step and cap rules are the API's, and it words its own rejections
        // ("A funnel must have between 2 and 6 steps", "A domain can have at
        // most 5 funnels") - show those rather than a paraphrase.
        setError(getErrorMessage(err));
        setSaving(false);
      });
  };

  let content;
  if (eventActions === undefined) {
    // Nothing else to show yet: the action multi-select cannot be filled in
    // until the domain's event actions are known.
    content = error ? <ErrorMessage message={error} /> : <Spinner />;
  } else {
    content = (
      <form onSubmit={save}>
        <Stack gap="sm">
          <TextInput
            label="Name"
            placeholder="Email clicks to purchase"
            value={draft.name}
            onChange={(event) => {
              // Read the value before the updater runs: React may run it later,
              // at render time, when the event's currentTarget is already null.
              const name = event.currentTarget.value;
              setDraft((current) => ({ ...current, name }));
            }}
          />

          <Select
            label="Audience segment"
            description="Optional. Only behavior segments can filter a funnel."
            placeholder="All profiles"
            clearable
            data={segmentOptions}
            value={draft.segmentId === null ? null : draft.segmentId.toString()}
            onChange={(value) =>
              setDraft((current) => ({ ...current, segmentId: value === null ? null : parseInt(value, 10) }))
            }
          />

          <Text size="sm" fw={600} mt="xs">
            Steps
          </Text>

          {draft.steps.map((step, index) => (
            <Card key={index} withBorder padding="sm">
              <Group justify="space-between" mb="xs">
                <Text size="sm" fw={600}>
                  Step {index + 1}
                </Text>
                <Group gap={4}>
                  <ActionIcon
                    variant="default"
                    aria-label={`Move step ${index + 1} up`}
                    disabled={index === 0}
                    onClick={() => setSteps(moveFunnelStep(draft.steps, index, -1))}
                  >
                    <IconArrowUp size={16} />
                  </ActionIcon>
                  <ActionIcon
                    variant="default"
                    aria-label={`Move step ${index + 1} down`}
                    disabled={index === draft.steps.length - 1}
                    onClick={() => setSteps(moveFunnelStep(draft.steps, index, 1))}
                  >
                    <IconArrowDown size={16} />
                  </ActionIcon>
                  <ActionIcon
                    variant="default"
                    color="red"
                    aria-label={`Remove step ${index + 1}`}
                    disabled={draft.steps.length <= MIN_FUNNEL_STEPS}
                    onClick={() => setSteps(draft.steps.filter((_, position) => position !== index))}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              </Group>

              <TextInput
                aria-label={`Step ${index + 1} name`}
                placeholder="Step name"
                value={step.name}
                onChange={(event) => updateStep(index, { name: event.currentTarget.value })}
              />

              <SegmentedControl
                mt="xs"
                fullWidth
                size="xs"
                data={stepKindOptions}
                value={step.kind}
                onChange={(kind) => updateStep(index, { kind })}
              />

              {step.kind === STEP_KIND_EVENT_MAPPING ? (
                <Select
                  mt="xs"
                  aria-label={`Step ${index + 1} standard event`}
                  description="Resolved against this domain's own action names when the funnel is measured."
                  placeholder="Select a standard event"
                  data={eventMappingOptions}
                  value={step.eventMapping}
                  allowDeselect={false}
                  onChange={(eventMapping) => updateStep(index, { eventMapping })}
                />
              ) : (
                <MultiSelect
                  mt="xs"
                  aria-label={`Step ${index + 1} event actions`}
                  description="The step is reached by any of the selected actions."
                  placeholder={step.actions.length === 0 ? 'Select event actions' : undefined}
                  searchable
                  data={eventActions}
                  value={step.actions}
                  onChange={(actions) => updateStep(index, { actions })}
                />
              )}
            </Card>
          ))}

          <Button
            variant="default"
            leftSection={<IconPlus size={16} />}
            disabled={draft.steps.length >= MAX_FUNNEL_STEPS}
            onClick={() => setSteps([...draft.steps, blankFunnelStep()])}
          >
            Add step
          </Button>

          {error && <ErrorMessage message={error} />}

          <Group justify="flex-end">
            <Button type="button" variant="default" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" loading={saving}>
              Save
            </Button>
          </Group>
        </Stack>
      </form>
    );
  }

  return (
    <Modal opened onClose={onClose} title={funnel ? 'Edit funnel' : 'Create funnel'} size="lg">
      {content}
    </Modal>
  );
};

export default FunnelFormModal;
